// Bailey, properly: a typed request goes to the salon's own Bailey (the agent
// on the server, POST /agent/social/ask) with the post, every choice the
// design offers and the salon's facts (menu, team, diary), plus their clients'
// reviews sent apart from the brief so the server can fence them off. No
// tools and no thread on the server: the studio keeps its own short history
// per visit. Bailey replies with the changes as JSON (the server holds the
// contract and the rules), and they're checked here before anything changes:
// only real fields, ready-made colours or readable ones, a client's review
// never reworded, and no number in the words that isn't in the request, the
// post or the facts (so no made-up prices, years or times). The rules in
// ask.js are the instant fallback when Bailey can't be reached.
import moment from 'moment'
import { agentService } from '../../../../../_services/agent.service'
import { FONTS, normHex } from '../../email-composer/email/theme'
import { POST_TYPES, TYPE_ORDER, changeType, restyle, fieldsFor, layoutsOf, layoutOf } from './postTypes'
import { FAMILIES, FAMILY_ORDER, DISPLAY_FONTS } from '../render/families'
import { colourwaysFor, colourwayInFamily, hardToRead, fixColours } from '../render/colourways'
import { TEXT_SIZES, postTheme } from '../render/render'
import { emptyFacts, findDays, JOB_SAID } from './ask'

const clean = (s) => String(s == null ? '' : s).replace(/\s+/g, ' ').trim()
// No dashes as punctuation, typographic apostrophes, one space
const tidyWords = (s) => clean(s)
  .replace(/\s*[—–]\s*/g, ', ')
  .replace(/\s-\s/g, ', ')
  .replace(/'/g, '’')
  .replace(/"([^"]*)"/g, '“$1”')
  .replace(/,\s*,/g, ',')
  .replace(/^,\s*|,\s*$/g, '')
const plain = (s) => String(s || '').replace(/\*/g, '')
const lower = (s) => { const t = clean(s); return t ? t.charAt(0).toLowerCase() + t.slice(1) : '' }
const pounds = (n) => { const v = Math.round(Number(n) * 100) / 100; return Number.isInteger(v) ? `£${v}` : `£${v.toFixed(2)}` }

// ─── Is Bailey there? ───
let ready = null
export const baileyReady = () => {
  if (!ready || (ready.failed && Date.now() - ready.at > 60 * 1000)) {
    const at = Date.now()
    ready = {
      at,
      job: agentService.status()
        .then((r) => { const d = r && r.data && (r.data.responseData || r.data); const ok = !!(d && d.available); if (!ok) ready.failed = true; return ok })
        .catch(() => { ready.failed = true; return false }),
    }
  }
  return ready.job
}
export const forgetBailey = () => { ready = null }

// ─── What Bailey is told ───
const FIELD_HOW = {
  list: (f) => ` (a list of up to ${f.max || 3} short items, like ["10am", "2pm"])`,
  priceList: () => ' (a list of {"name", "price", "detail"})',
  toggle: () => ' (true or false)',
  date: () => ' (YYYY-MM-DD)',
  stars: () => ' (1 to 5)',
  pairs: (f) => ` (a list of up to ${f.max || 3} {"q", "a"}, like {"q": "My approach", "a": "Time to listen"})`,
}
const ROLE_TEXT = {
  editorial: 'paper is the background, ink is the words and the dark panels, accent is a soft colour used on dark panels, pop is a lighter soft colour',
  poster: 'accent is the big background colour, paper is the words on it and the light panels, ink is the dark words inside the light panels (pop is the same as paper)',
  quiet: 'paper is the background, ink is the words, accent is a second soft background, pop is a small highlight',
  layouts: 'paper is the background, ink is the words on it, accent is the strong colour for blocks and discs (and the background of the bold layouts), on is the words on the accent, pop is a soft block',
}
const fontName = (k) => (FONTS[k] && FONTS[k].label) || k
const q = (v) => JSON.stringify(v === undefined ? '' : v)

// reviewsApart: the brief names the reviews' block rather than repeating
// them (they're sent separately, see reviewsFor). applyEdit reads them all.
const factsText = (facts = {}, now, { reviewsApart = false } = {}) => {
  const lines = []
  if (facts.services && facts.services.length) {
    lines.push(`Menu (name, price, time): ${facts.services.filter(s => s && s.name).slice(0, 40).map(s => `${s.name} ${Number(s.price) > 0 ? pounds(s.price) : 'no price'}${s.duration ? ` (${s.duration})` : ''}`).join('; ')}`)
  }
  if (facts.team && facts.team.length) lines.push(`Team: ${facts.team.slice(0, 20).map(m => `${m.name}${m.role ? ` (${m.role})` : ''}`).join('; ')}`)
  if (facts.freeDay && facts.freeDay.times && facts.freeDay.times.length) {
    const all = facts.freeDay.all && facts.freeDay.all.length ? facts.freeDay.all : facts.freeDay.times
    const whose = facts.freeDay.who ? `${facts.freeDay.who.firstName}’s diary` : 'the diary'
    lines.push(`Free appointment times in ${whose} on ${moment(facts.freeDay.date).format('dddd D MMMM')}: ${all.slice(0, 24).join(', ')}`)
  } else if (facts.diaryRead) lines.push(facts.freeNamed ? `${facts.freeNamed} has no free gaps on that day.` : 'The diary has no free gaps on that day.')
  if (facts.vouchers && facts.vouchers.length) {
    const min = Math.min(...facts.vouchers.map(v => Number(v.fromPrice || v.value) || Infinity))
    if (Number.isFinite(min)) lines.push(`Gift vouchers on sale online, from ${pounds(min)}`)
  }
  if (facts.reviews && facts.reviews.length && reviewsApart) {
    lines.push('Reviews: in <client_reviews> (id, name, stars, words). Pick one by its id.')
  } else if (facts.reviews && facts.reviews.length) {
    lines.push('Reviews (id, name, stars, words):')
    facts.reviews.filter(r => r && r.text).slice(0, 12).forEach((r) => lines.push(`  ${r.id}: ${r.firstName || r.author || 'A client'}, ${r.rating} stars, ${q(r.text)}`))
  }
  return lines
}

// The salon's reviews for Bailey, apart from the brief: they're written by
// clients, so the server puts them in their own block as data
export const reviewsFor = (facts = {}) => (facts.reviews || [])
  .filter(r => r && r.text && r.id != null)
  .slice(0, 12)
  .map(r => ({ id: String(r.id), name: r.firstName || r.author || 'A client', stars: Number(r.rating) || null, text: String(r.text) }))

// The whole brief: the request first, then the post, the choices and the
// facts. What to reply with, and the rules, are the server's (one place)
export const briefFor = (post, { text, kit = {}, trade = 'hair', facts = {}, now = new Date(), fresh = false } = {}) => {
  const def = POST_TYPES[post.type]
  const locked = post.type === 'review' && /^review:/.test(post.source || '')
  const theme = postTheme(post, { kit })
  const fam = post.family || 'editorial'
  const ways = colourwaysFor(fam, { brandColour: kit.accent, photoColour: post.photoColour })
  const photos = def.photos.map(p => `${p.label}${post.photos && post.photos[p.key] && post.photos[p.key].src ? '' : ' (empty)'}`)
  const filter = def.photos.map(p => post.photos && post.photos[p.key] && post.photos[p.key].filter).find(Boolean) || 'none'
  const lines = [
    clean(text),
    '',
    `(Sent from the post studio. ${fresh ? 'This is a new post: fill it in from what I asked.' : 'Change the post below to do what I asked.'} Reply with one JSON object and nothing else.)`,
    '',
    `POST: ${def.label} (kind "${post.type}"), ${post.format === 'story' ? 'an Instagram Story, 9:16' : 'an Instagram feed post, 4:5'}.`,
    'WORDS (key: what it is = now):',
    ...fieldsFor(post).map(f => `- ${f.key}: ${f.label}${(FIELD_HOW[f.type] || (() => ''))(f)}${f.key === 'quote' && locked ? ' [a client’s review: never change it]' : ''} = ${q(post.content[f.key])}`),
    '*Asterisks* around words set them in italics in the editorial and quiet styles.',
    fam === 'layouts'
      ? `STYLE now: the layout "${post.layout}" (${(layoutOf(post.type, post.layout) || {}).name}). Other layouts for this kind: ${layoutsOf(post.type).filter(l => l !== post.layout).map(l => `${l} (${layoutOf(post.type, l).name})`).join(', ')}.${def.designs && Object.keys(def.designs).length ? ` Styles: ${FAMILY_ORDER.map(f => `${f} (${FAMILIES[f].blurb})`).join(', ')}.` : ''}`
      : `STYLE now: ${fam} (${FAMILIES[fam].blurb}). Other styles: ${FAMILY_ORDER.filter(f => f !== fam).map(f => `${f} (${FAMILIES[f].blurb})`).join(', ')}.${layoutsOf(post.type).length ? ` Layouts for this kind: ${layoutsOf(post.type).map(l => `${l} (${layoutOf(post.type, l).name})`).join(', ')}.` : ''}`,
    `COLOURS now: paper ${theme.c.paper}, ink ${theme.c.ink}, accent ${theme.c.accent}, pop ${theme.c.pop}${fam === 'layouts' ? `, on ${theme.c.on}` : ''}. In this style ${ROLE_TEXT[fam]}.`,
    `READY-MADE COLOURS for this style: ${ways.map(w => `${w.id} (${w.name}: ${w.colours.join(' ')})`).join('; ')}.`,
    `HEADLINE FONTS: ${Object.keys(DISPLAY_FONTS).map(k => `${k} (${fontName(k)}, ${DISPLAY_FONTS[k].serif ? 'serif' : 'sans'})`).join(', ')}. Now: ${theme.family.display}.`,
    `TEXT SIZE now: ${TEXT_SIZES.indexOf(theme.size) + 1} of ${TEXT_SIZES.length}.`,
    photos.length ? `PHOTOS: ${photos.join(', ')}. Filter now: ${filter}. Filters: bright, warm, cool, mono, none.` : 'PHOTOS: none in this kind of post.',
    post.plannedAt ? `PLANNED for ${moment(post.plannedAt).format('dddd D MMMM, h:mma')}.` : 'Not planned yet.',
    `SALON: ${kit.name || 'the salon'}, a ${trade === 'barber' ? 'barber' : trade === 'beauty' ? 'beauty salon' : 'hair salon'}${kit.accent ? `, brand colour ${kit.accent}` : ''}. Now: ${moment(now).format('dddd D MMMM YYYY, h:mma')}.`,
    'OTHER KINDS OF POST (only if I ask for a different kind, or this kind doesn’t fit what I asked): ' + TYPE_ORDER.filter(t => t !== post.type).map(t => `${t} (${POST_TYPES[t].label}: ${POST_TYPES[t].fields.map(f => f.key).join(', ')})`).join('; ') + '. For salon news with no photo (closures, holidays, opening hours), use tips: label is a short heading like "Salon news", number is empty, headline is the news, body is the detail.',
  ]
  const facts2 = factsText(facts, now, { reviewsApart: true })
  if (facts2.length) lines.push('FACTS you may use:', ...facts2)
  return lines.join('\n')
}

// ─── Asking ───
// { ok, edit, history, error }. Nothing is kept on the server: `history` is
// this studio visit's thread (the salon's words and Bailey's replies), sent
// back each time so follow-ups ("brighter still") read in context.
const HISTORY_KEEP = 12
export const askBailey = ({ brief, text = '', reviews = [], history = [], timeout = 45000 }) => new Promise((resolve) => {
  const prior = Array.isArray(history) ? history : []
  let settled = false
  let request = null
  const finish = (out) => {
    if (settled) return
    settled = true
    clearTimeout(timer) // eslint-disable-line no-use-before-define
    resolve({ history: prior, ...out })
  }
  const timer = setTimeout(() => { if (request && request.cancel) request.cancel(); finish({ ok: false, error: 'timeout' }) }, timeout)
  try {
    request = agentService.socialAsk({ brief, reviews, history: prior.slice(-HISTORY_KEEP) })
  } catch (e) {
    finish({ ok: false, error: 'error' })
    return
  }
  Promise.resolve(request).then((res) => {
    const body = res && res.data
    const edit = body && body.responseData && body.responseData.edit
    if (!edit || typeof edit !== 'object' || Array.isArray(edit)) {
      const err = body && body.error
      finish({ ok: false, error: (err && err.errors && err.errors.reason) || 'unreadable' })
      return
    }
    const said = clean(text) || clean(String(brief || '').split('\n')[0])
    finish({ ok: true, edit, history: [...prior, { role: 'user', text: said }, { role: 'assistant', text: JSON.stringify(edit) }].slice(-HISTORY_KEEP) })
  }).catch(() => finish({ ok: false, error: 'error' }))
})

// The JSON in Bailey's reply (with or without a code fence around it)
export const readReply = (content) => {
  const s = String(content || '')
  const a = s.indexOf('{')
  const b = s.lastIndexOf('}')
  if (a < 0 || b <= a) return null
  try {
    const j = JSON.parse(s.slice(a, b + 1))
    return j && typeof j === 'object' && !Array.isArray(j) ? j : null
  } catch (e) {
    return null
  }
}

// ─── Checking and applying ───
const numbersIn = (s) => (String(s == null ? '' : s).match(/\d+(?:[.:]\d+)?/g) || []).map(n => String(Number(n.replace(':', '.'))))
// Prices worked out from the menu and the discount asked for
const derivedPrices = (text, services) => {
  const out = []
  const pct = (String(text).match(/(\d{1,3})\s?(?:%|percent|per cent)/gi) || []).map(m => parseInt(m, 10))
  const off = (String(text).match(/£\s?(\d+(?:\.\d{1,2})?)\s?off/gi) || []).map(m => Number(m.replace(/[^\d.]/g, '')))
  if (/half[\s-]?price|half off/i.test(text)) pct.push(50)
  ;(services || []).forEach((s) => {
    const p = Number(s.price)
    if (!(p > 0)) return
    pct.forEach(x => out.push(Math.round(p * (1 - x / 100) * 100) / 100))
    off.forEach(x => out.push(Math.round((p - x) * 100) / 100))
  })
  return out.map(String)
}

const GROUND = { editorial: 'paper', poster: 'accent', quiet: 'paper', layouts: 'paper' }
const SIZE_STEP = { bigger: 1, larger: 1, smaller: -1 }
const FILTERS = ['bright', 'warm', 'cool', 'mono', 'none']
const FILTER_SAID = { mono: 'Photo in black and white', warm: 'Warmer photo', cool: 'Cooler photo', bright: 'Brighter photo', none: 'Photo back to its own colours' }
const whenSaid = (m, now) => moment(m).calendar(now, { sameDay: '[today at] h:mma', nextDay: '[tomorrow at] h:mma', nextWeek: 'dddd [at] h:mma', sameElse: 'ddd D MMM [at] h:mma' }).replace(':00', '')

// Bailey's changes, checked, as { post, changes, needs, notes, say, given }
export const applyEdit = (post, edit, ctx = {}) => {
  const now = ctx.now || new Date()
  const kit = ctx.kit || {}
  const changes = []
  const needs = []
  const notes = []
  let rejected = 0
  const need = (text, field) => { if (text && !needs.some(n => n.text === text)) needs.push({ text, field: field || null }) }
  const e = edit && typeof edit === 'object' ? edit : {}

  // A different kind of post, when asked for
  let next = post
  let switched = false
  if (typeof e.kind === 'string' && TYPE_ORDER.includes(e.kind) && e.kind !== post.type) {
    next = changeType(post, e.kind)
    switched = true
    changes.push(`Made it ${JOB_SAID[e.kind]}`)
  }
  // A different style or layout, when asked for
  let tune = { ...(next.tune || {}) }
  if (typeof e.layout === 'string' && layoutOf(next.type, e.layout) && !(next.family === 'layouts' && next.layout === e.layout)) {
    const fromStyle = next.family !== 'layouts'
    next = restyle(next, 'layouts', colourwayInFamily(next.colourway, next.family, 'layouts'), e.layout)
    delete tune.colours
    if (fromStyle) { delete tune.font; delete tune.textFont }
    changes.push(`Style: ${layoutOf(next.type, e.layout).name}`)
  } else if (typeof e.style === 'string' && FAMILY_ORDER.includes(e.style) && e.style !== next.family) {
    next = restyle(next, e.style, colourwayInFamily(next.colourway, next.family, e.style))
    delete tune.colours
    delete tune.font
    delete tune.textFont
    changes.push(`Style: ${FAMILIES[next.family].name}`)
  }
  const type = next.type
  const def = POST_TYPES[type]
  const fam = next.family || 'editorial'
  const locked = type === 'review' && /^review:/.test(next.source || '')
  const content = { ...next.content }
  const touched = { ...(next.touched || {}) }
  let photos = next.photos || {}
  let source = next.source
  const given = new Set()

  // Numbers Bailey may use: the request's, the post's, the facts'
  // (and the dates the request means: "next Monday" is the 5th)
  const dates = findDays(String(ctx.text || ''), now).map(d => d.date)
  const allowed = new Set([
    ...numbersIn(ctx.text), ...numbersIn(JSON.stringify(post.content || {})),
    ...numbersIn(factsText(ctx, now).join(' ')), ...derivedPrices(ctx.text, ctx.services),
    ...dates.map(d => String(d.date())), String(moment(now).year()), String(moment(now).year() + 1),
  ])
  const checked = (s) => numbersIn(s).every(n => allowed.has(n))

  // A review, word for word, picked from the salon's own
  if (type === 'review' && e.review) {
    const r = (ctx.reviews || []).find(x => x && String(x.id) === String(e.review))
    if (r) {
      Object.assign(content, { quote: r.text, name: (r.firstName || r.author || 'A client').split(' ')[0], service: r.service || '', rating: Math.max(1, Math.min(5, Number(r.rating) || 5)) })
      ;['quote', 'name', 'service', 'rating'].forEach((k) => { touched[k] = true; given.add(k) })
      source = `review:${r.id}`
      changes.push(`${content.name}’s ${content.rating} star review, word for word`)
    }
  }

  // The words
  const words = e.words && typeof e.words === 'object' ? e.words : {}
  Object.keys(words).forEach((key) => {
    const f = def.fields.find(x => x.key === key)
    if (!f || given.has(key)) return
    if (key === 'quote' && locked) { notes.push('A client’s review stays word for word, so I left it as it is.'); return }
    const v = words[key]
    let value
    if (f.type === 'list') {
      if (!Array.isArray(v)) return
      value = v.map(x => tidyWords(x)).filter(Boolean).slice(0, f.max || 3)
      if (!value.every(checked)) { rejected += 1; need(`${f.label}: I couldn’t check those, so add them yourself`, key); return }
    } else if (f.type === 'priceList') {
      if (!Array.isArray(v)) return
      value = v.filter(x => x && x.name).slice(0, f.max || 12).map(x => ({ name: tidyWords(x.name), price: tidyWords(x.price), detail: tidyWords(x.detail) }))
      if (!value.every(x => checked(x.price) && checked(x.detail))) { rejected += 1; need('Your prices: I couldn’t check them, so add them in Text', key); return }
    } else if (f.type === 'toggle') {
      if (typeof v !== 'boolean') return
      value = v
    } else if (f.type === 'date') {
      if (!/^\d{4}-\d{2}-\d{2}$/.test(String(v)) || !moment(v, 'YYYY-MM-DD', true).isValid()) return
      value = String(v)
    } else if (f.type === 'stars') {
      const n = Math.round(Number(v))
      if (!(n >= 1 && n <= 5) || locked) return
      value = n
    } else {
      if (typeof v !== 'string') return
      value = tidyWords(v).slice(0, 240)
      if (!checked(value)) { rejected += 1; notes.push(`I left the ${lower(f.label)} as it was: it had a number I couldn’t check.`); return }
    }
    if (JSON.stringify(value) === JSON.stringify(content[key])) return
    content[key] = value
    touched[key] = true
    given.add(key)
    const shown = Array.isArray(value) ? (f.type === 'priceList' ? value.map(i => `${i.name} ${i.price}`).join(', ') : value.join(', ')) : f.type === 'toggle' ? (value ? 'on' : 'off') : String(value)
    changes.push(`${f.label}: ${f.type === 'text' || f.type === 'textarea' ? `“${plain(shown)}”` : shown}`)
  })

  // A new kind of post keeps none of the template's example facts
  if (switched) emptyFacts(type, { content, touched, photos, given, need })

  // Colours: a ready-made set, or four of Bailey's own made readable
  const ways = colourwaysFor(fam, { brandColour: kit.accent, photoColour: next.photoColour })
  let colourway = next.colourway
  if (typeof e.colourway === 'string' && ways.some(w => w.id === e.colourway) && !e.colours) {
    if (e.colourway !== colourway || tune.colours) {
      colourway = e.colourway
      delete tune.colours
      changes.push(`Colours: ${lower(ways.find(w => w.id === e.colourway).name)}`)
    }
  }
  if (e.colours && typeof e.colours === 'object') {
    const c = postTheme({ ...next, colourway, tune }, { kit }).c
    const asked = {}
    ;['paper', 'ink', 'accent', 'pop', ...(fam === 'layouts' ? ['on'] : [])].forEach((k) => { const h = normHex(e.colours[k]); if (h) asked[k] = h.toUpperCase() })
    if (Object.keys(asked).length) {
      let full = { paper: c.paper, ink: c.ink, accent: c.accent, pop: c.pop, ...(fam === 'layouts' ? { on: c.on } : {}), ...asked }
      if (fam === 'poster' && asked.paper) full.pop = asked.paper
      if (hardToRead(fam, full).length) {
        full = { ...full, ...fixColours(fam, full, GROUND[fam]) }
        if (fam === 'poster') full.pop = full.paper
        notes.push('I adjusted a colour so the words stay easy to read.')
      }
      tune.colours = { paper: full.paper, ink: full.ink, accent: full.accent, pop: full.pop, ...(fam === 'layouts' ? { on: full.on } : {}) }
      const name = tidyWords(e.colourName).replace(/[.!]$/, '')
      changes.push(`Colours: ${name ? lower(name) : 'new colours'}`)
    }
  }

  // Font, size, format, photo filter
  if (typeof e.font === 'string' && DISPLAY_FONTS[e.font] && e.font !== postTheme({ ...next, tune }, { kit }).family.display) {
    tune.font = e.font
    changes.push(`Headline font: ${fontName(e.font)}`)
  }
  const step = SIZE_STEP[String(e.size || '').toLowerCase()]
  if (step) {
    const cur = TEXT_SIZES.indexOf(TEXT_SIZES.includes(Number(tune.size)) ? Number(tune.size) : 1)
    const to = Math.max(0, Math.min(TEXT_SIZES.length - 1, cur + step))
    if (to !== cur) {
      if (TEXT_SIZES[to] === 1) delete tune.size
      else tune.size = TEXT_SIZES[to]
      changes.push(step > 0 ? 'Bigger words' : 'Smaller words')
    }
  }
  let format = next.format || 'post'
  if ((e.format === 'post' || e.format === 'story') && e.format !== format) {
    format = e.format
    changes.push(format === 'story' ? 'Now a Story' : 'Now a feed post')
  }
  if (typeof e.photo === 'string' && FILTERS.includes(e.photo)) {
    const keys = Object.keys(photos).filter(k => photos[k] && photos[k].src)
    const now2 = keys.map(k => photos[k].filter || 'none')
    if (keys.length && now2.some(fl => fl !== e.photo)) {
      photos = { ...photos }
      keys.forEach((k) => {
        const ph = { ...photos[k] }
        if (e.photo === 'none') delete ph.filter
        else ph.filter = e.photo
        photos[k] = ph
      })
      changes.push(FILTER_SAID[e.photo])
    }
  }

  // When to post it
  let plannedAt = next.plannedAt || null
  let status = next.status || 'draft'
  if (typeof e.postAt === 'string' && e.postAt) {
    const at = moment(e.postAt, ['YYYY-MM-DDTHH:mm', 'YYYY-MM-DDTHH:mm:ss', 'YYYY-MM-DD HH:mm'], true)
    if (at.isValid() && at.isAfter(moment(now))) {
      plannedAt = at.toISOString()
      if (status !== 'posted') status = 'planned'
      changes.push(`Planned for ${whenSaid(at, now)}`)
    } else if (at.isValid()) need('A later time to post it. That time has gone')
  }

  let out = { ...next, colourway, content, touched, photos, source, format, plannedAt, status, tune: Object.keys(tune).length ? tune : undefined }

  // The caption: written again when the words changed (unless the salon
  // wrote it), in a new voice, or with hashtags added
  const old = next.caption
  let caption = old
  if ((given.size || switched) && !(old && old.edited)) caption = null
  const cap = e.caption && typeof e.caption === 'object' ? e.caption : {}
  const voice = ['warm', 'short', 'fun'].includes(cap.voice) ? cap.voice : null
  const tags = Array.isArray(cap.add) ? cap.add.map(t => `#${String(t).replace(/[^A-Za-z0-9_]/g, '')}`).filter(t => t.length > 2).slice(0, 5) : []
  if ((voice || tags.length) && ctx.captionFor) {
    let base = voice ? ctx.captionFor(out, voice) : (caption || ctx.captionFor(out, (old && old.voice) || 'warm'))
    if (voice) changes.push(`Caption: ${voice === 'fun' ? 'playful' : voice}`)
    if (tags.length) { base = { ...base, hashtags: Array.from(new Set([...(base.hashtags || []), ...tags])), edited: true }; changes.push(`Added ${tags.join(' ')}`) }
    caption = base
  }
  out = { ...out, caption: caption || null }

  // Bailey's own questions, answered by replying
  ;(Array.isArray(e.need) ? e.need : []).slice(0, 3).forEach((n) => { const t = tidyWords(n); if (t && !needs.some(x => x.text === t)) needs.push({ text: t, field: null, ask: true }) })
  // Bailey's own line, when everything it did went in
  const say = !rejected && typeof e.say === 'string' ? tidyWords(e.say).slice(0, 200) : ''
  if (!changes.length && !ctx.fresh) return { post, changes, needs, notes, say, given: [] }
  return { post: out, changes, needs, notes, say, given: Array.from(given) }
}

// A post Bailey made, as what's on it: its kind, its words, then how it looks
// and when it goes (from Bailey's own list)
const LOOK_LINE = /^(Style|Colours|Headline font|Bigger words|Smaller words|Now a|Photo|Warmer photo|Cooler photo|Brighter photo|Caption|Added|Planned)/
// A tip with no number is salon news (closures, holidays, new hours)
export const isNews = (post) => !!post && post.type === 'tips' && !clean(post.content && post.content.number)
const NEWS_LABELS = { label: 'Small heading', body: 'Details' }
export const summaryOf = (post, lines = [], now = new Date()) => {
  const def = POST_TYPES[post.type]
  const news = isNews(post)
  const out = [`Made ${news ? 'a salon news post' : JOB_SAID[post.type]}${post.format === 'story' ? ' as a Story' : ''}`]
  def.fields.forEach((raw) => {
    const f = news && NEWS_LABELS[raw.key] ? { ...raw, label: NEWS_LABELS[raw.key] } : raw
    const v = post.content[f.key]
    if (!(post.touched || {})[f.key] || v === '' || v == null || (Array.isArray(v) && !v.length) || f.type === 'toggle') return
    if (f.type === 'date') out.push(`${f.label}: ${moment(v).calendar(now, { sameDay: '[today]', nextDay: '[tomorrow]', nextWeek: 'dddd', sameElse: 'ddd D MMM' })}`)
    else if (f.type === 'stars') out.push(`${f.label}: ${v}`)
    else if (f.type === 'priceList') out.push(`${f.label}: ${v.map(i => `${i.name} ${i.price}`).join(', ')}`)
    else if (f.type === 'pairs') out.push(`${f.label}: ${v.map(i => [i.q, i.a].filter(Boolean).join(': ')).join(', ')}`)
    else if (Array.isArray(v)) out.push(`${f.label}: ${v.join(', ')}`)
    else out.push(`${f.label}: “${plain(v)}”`)
  })
  lines.filter(l => LOOK_LINE.test(l)).forEach((l) => { if (!out.includes(l)) out.push(l) })
  if (post.plannedAt && !out.some(l => /^Planned/.test(l))) out.push(`Planned for ${whenSaid(post.plannedAt, now)}`)
  return out
}
